import { useState } from "react";
import { Menu, Phone, X, ShoppingBag, BookOpen, Star, HelpCircle, PhoneCall } from "lucide-react";
import Image from "next/image";
import { scrollToSection } from "@/lib/scroll";
import { Button } from "@/components/v2/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/v2/ui/sheet";
import UserMenu from "@/components/v2/commons/UserMenu";
import UserMenuMobile from "@/components/v2/commons/UserMenuMobile";

import * as Icons from "lucide-react";
const bmpLogo = "/images/bmp.png";

const Navbar = ({ navData, topSlot = null }) => {
  const [open, setOpen] = useState(false);

  const NAV_LINKS = navData?.links || [];
  const MENU_ITEMS = (navData?.menuItems || []).map(item => ({
    ...item,
    icon: Icons[item.icon] || Icons.HelpCircle
  }));

  const handleNavClick = (href) => {
    setOpen(false);
    if (href.startsWith("#")) {
      scrollToSection(href.slice(1));
    }
  };

  const handleWhatsAppClick = (e) => {
    e.preventDefault();
    const referenceText = `Reference: ${window.location.href}`;
    const message = encodeURIComponent(`Hi! I'd like to chat with a Puja Advisor about booking a ceremony.\n\n${referenceText}`);
    window.open(`https://wa.me/919869929292?text=${message}`, '_blank');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b">
      {topSlot}
      <div className="container flex items-center justify-between py-3">
        <div className="relative h-14 md:h-16 w-32 md:w-40">
          <Image
            src={bmpLogo}
            alt="BookMyPooja Online Puja Services Logo"
            fill
            className="object-contain"
            priority
            sizes="(max-width: 768px) 128px, 160px"
          />
        </div>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-6 text-sm font-medium text-muted-foreground">
          {NAV_LINKS.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
              className="hover:text-primary transition-colors font-sans"
            >
              {link.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <a href={navData?.phoneHref || "tel:9869929292"} className="hidden md:flex items-center gap-1.5 text-sm font-semibold text-primary font-sans mr-2">
            <Icons.Phone className="h-4 w-4" />
            {navData?.phone || "9869 92 92 92"}
          </a>

          <div className="hidden md:block">
            <UserMenu />
          </div>

          {/* Mobile hamburger */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 p-0">
              <div className="flex flex-col h-full">
                <div className="p-4 border-b relative h-10 w-32">
                  <Image src={bmpLogo} alt="BookMyPooja Online Puja Services Logo" fill className="object-contain" sizes="128px" />
                </div>

                {/* Profile section at TOP */}
                <UserMenuMobile onClose={() => setOpen(false)} mode="accordion" />

                <div className="flex-1 py-2">
                  {MENU_ITEMS.map((item) => (
                    <a
                      key={item.label}
                      href={item.external ? item.href : undefined}
                      target={item.external ? "_blank" : undefined}
                      rel={item.external ? "noopener noreferrer" : undefined}
                      onClick={item.external ? undefined : (e) => { e.preventDefault(); handleNavClick(item.href); }}
                      className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground hover:bg-accent transition-colors"
                    >
                      <item.icon className="h-4 w-4 text-muted-foreground" />
                      {item.label}
                    </a>
                  ))}
                </div>


                <div className="border-t p-4 space-y-2">
                  <a
                    href={navData?.phoneHref || "tel:+919869929292"}
                    className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
                  >
                    <Icons.PhoneCall className="h-4 w-4 text-primary" />
                    {navData?.phone || "9869 92 92 92"}
                  </a>
                  <a
                    href={navData?.whatsapp?.href || "https://wa.me/919869929292"}
                    onClick={handleWhatsAppClick}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-[hsl(var(--whatsapp))] transition-colors"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4 text-[hsl(var(--whatsapp))]">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    {navData?.whatsapp?.label || "WhatsApp Us"}
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
